import React, { useEffect } from 'react';
import type { PropsWithChildren } from 'react';

import type { Node } from '@atlaskit/editor-prosemirror/model';

import { ReactSerializer } from '@atlaskit/renderer';
import type { ReactSerializerInit } from '@atlaskit/renderer';

import { NullRenderer } from './NullRenderer';

// Maximum estimated height for the above-the-fold content
const MAX_ESTIMATED_HEIGHT = 600;

export class AboveTheFoldReactSerializer extends ReactSerializer {
	private onlySerializeFirstBatch: boolean;
	private onBatchRendered?: (done: boolean) => void;

	private estimatedHeight: number;

	constructor(
		init: ReactSerializerInit,
		onlySerializeFirstBatch: boolean = false,
		onBatchRendered?: (done: boolean) => void,
	) {
		super(init);

		this.onlySerializeFirstBatch = onlySerializeFirstBatch;
		this.onBatchRendered = onBatchRendered;

		this.estimatedHeight = 0;
	}

	// Calculates the estimated height of a node based on its type.
	// We underestimate the heights to avoid rendering too few nodes.
	private getEstimatedHeight(node: Node): number {
		if (node?.type?.name === 'paragraph' && !!node?.content?.size) {
			return 30;
		} else {
			return 0;
		}
	}

	/**
	 * Like {@link ReactSerializer.resetState}. Named differently because a class cannot have a separate declaration of a private property of its base class.
	 */
	private resetProgressiveState() {
		this.estimatedHeight = 0;
	}

	/**
	 * @override
	 */
	serializeFragment(
		_fragment: SerializeFragmentParameters[0],
		_props?: SerializeFragmentParameters[1],
		_target?: SerializeFragmentParameters[2],
		key?: SerializeFragmentParameters[3],
		parentInfo?: SerializeFragmentParameters[4],
	): JSX.Element | null {
		const isRoot = key === undefined; // Like ReactSerializer.serializeFragment
		if (isRoot) {
			this.resetProgressiveState();
		}

		// Top level nodes have a parent path of length 1.
		if (parentInfo?.path?.length === 1) {
			this.estimatedHeight += this.getEstimatedHeight(parentInfo?.path?.[0]);
		}

		const shouldSkipNodeSerialization =
			this.onlySerializeFirstBatch &&
			// Skip serialization of anything estimated to be below the fold.
			this.estimatedHeight >= MAX_ESTIMATED_HEIGHT;

		if (shouldSkipNodeSerialization) {
			// Returning a component, as opposed to null, is important to avoid breaking existing logic
			// within ADF node components that assume their children are React elements. NullRenderer
			// renders nothing, but is still a valid React element.
			return <NullRenderer />;
		}

		const nodeElement = super.serializeFragment.apply(
			this,
			arguments as unknown as SerializeFragmentParameters,
		);

		if (isRoot) {
			return (
				<OnBatchRenderedComponent
					onBatchRendered={this.onBatchRendered}
					onlySerializeFirstBatch={this.onlySerializeFirstBatch}
				>
					{nodeElement}
				</OnBatchRenderedComponent>
			);
		} else {
			return nodeElement;
		}
	}
}

type OnBatchRenderedComponentProps = PropsWithChildren<{
	onBatchRendered: ((done: boolean) => void) | undefined;
	onlySerializeFirstBatch: boolean;
}>;

const OnBatchRenderedComponent = ({
	children,
	onBatchRendered,
	onlySerializeFirstBatch,
}: OnBatchRenderedComponentProps) => {
	useEffect(() => {
		// eslint-disable-next-line compat/compat
		const requestIdleCallback = window.requestIdleCallback || window.requestAnimationFrame;
		const cancelHandle = requestIdleCallback(() => {
			onBatchRendered?.(!onlySerializeFirstBatch);
		});

		return () => {
			const cancelIdleCallback = window.cancelIdleCallback || window.cancelAnimationFrame;
			cancelIdleCallback(cancelHandle);
		};
	}, [onBatchRendered, onlySerializeFirstBatch]);

	return children;
};

type SerializeFragmentParameters = Parameters<ReactSerializer['serializeFragment']>;
